import React, { useEffect, useMemo, useRef, useState } from "react";
import { collection, doc, getFirestore, serverTimestamp, writeBatch } from "firebase/firestore";
import { GuidanceUser, RecruitItem } from "../../index";
import Styles from "./styles.module.css";
import { rankDisplay, rankLabel } from "../../config";
import usePageBackground from "../../usePageBackground";

// 学生アプリのガイダンスの志望の画面(lib/5.account/1.items/guidance/3.order.dart)と同じ画面。
//   志望群の学校: サロンごとに「第1志望群(3つまで)/第2志望群(3つまで)/未選択」を選ぶ。
//   順位付けの学校: ≡ をドラッグして並べ替える(区切りより上が第1志望〜)か、第何志望かを選ぶ。
//   アプリは下から出るシートで選ぶが、Web は学生情報入力のコース・学年・クラスと同じ選択式(端末標準の選択画面)にしている。
// Web はログインしないので、選んだ内容は「確定」でまとめて guidance/{学校名_学年_クラス_コース_名前} に保存する。

type Props = {
  userInfo: GuidanceUser;
  likes: RecruitItem[];
  firstList: string[];
  setFirstList: React.Dispatch<React.SetStateAction<string[]>>;
  secondList: string[];
  setSecondList: React.Dispatch<React.SetStateAction<string[]>>;
  rankCount: number;
  rankList: string[];
  setRankList: React.Dispatch<React.SetStateAction<string[]>>;
  onBack: () => void;
};

type GroupStatus = "第1志望群" | "第2志望群" | "未選択";

const PINK = "#E44E70";
const ORANGE = "#F2941A";
const GRAY = "#999999";

const logoUrl = (companyId: string) =>
  `https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o/companies_logo%2Fresize_images%2F${companyId}_200x200?alt=media&token=`;

const recruitUrl = (recruitId: string) => `https://memorii-works.com/recruit/id=${recruitId}&job`;

const DIVIDER = "__divider__";

export default function GuidanceOrder({
  userInfo, likes, firstList, setFirstList, secondList, setSecondList, rankCount, rankList, setRankList, onBack,
}: Props) {

  const [ loading, setLoading ] = useState(false);
  const [ submittedDialog, setSubmittedDialog ] = useState(false);
  const [ submitted, setSubmitted ] = useState(false);
  const [ rankErrorsOpen, setRankErrorsOpen ] = useState(false);
  usePageBackground("#F5F5F5");

  // 受付完了の画面に切り替わったら一番上から見せる
  useEffect(() => {
    if (submitted) window.scrollTo(0, 0);
  }, [submitted]);

  const firstCount = firstList.length;
  const secondCount = secondList.length;

  // 順位付けの学校。slots[i] が第(i+1)志望の recruitId(空きは "")。いいねを外したサロンは空きにする。
  const rankMode = rankCount > 0;
  const likedIds = likes.map((l) => l.recruitId);
  const slots = useMemo(
    () => Array.from({ length: rankCount }, (_, i) => (likedIds.includes(rankList[i] ?? "") ? rankList[i] : "")),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rankCount, rankList, likes],
  );
  const missingRanks = slots.flatMap((id, i) => (id ? [] : [i + 1]));
  const rankErrors = missingRanks.map((r) => `${rankDisplay(r)}がまだ選ばれていません`);

  const canSubmit = rankMode ? missingRanks.length === 0 : firstCount === 3 && secondCount === 3;

  const rankOfId = (recruitId: string) => {
    const i = slots.indexOf(recruitId);
    return i < 0 ? null : i + 1;
  };

  // 第rank志望にする(null は未選択に戻す)。ほかのサロンが使っている志望を選ぶと入れ替わる(アプリと同じ)。
  function setRank(recruitId: string, rank: number | null) {
    const next = [...slots];
    const current = next.indexOf(recruitId);
    if (rank === null) {
      if (current >= 0) next[current] = "";
    } else {
      const other = next[rank - 1];
      if (current >= 0) next[current] = other && other !== recruitId ? other : "";
      next[rank - 1] = recruitId;
    }
    setRankList(next);
  }

  function getStatus(recruitId: string): GroupStatus {
    if (firstList.includes(recruitId)) return "第1志望群";
    if (secondList.includes(recruitId)) return "第2志望群";
    return "未選択";
  }

  function setStatus(recruitId: string, value: GroupStatus) {
    setFirstList((prev) => prev.filter((v) => v !== recruitId));
    setSecondList((prev) => prev.filter((v) => v !== recruitId));
    if (value === "第1志望群") setFirstList((prev) => [...prev, recruitId]);
    else if (value === "第2志望群") setSecondList((prev) => [...prev, recruitId]);
  }

  async function handleSubmit() {
    if (loading) return;
    if (rankMode && rankErrors.length > 0) {
      setRankErrorsOpen(true);
      return;
    }
    if (!canSubmit) return;

    try {
      setLoading(true);
      const db = getFirestore();

      // ドキュメントID: 学校名_学年_クラス_コース_名前 (空白除去)
      const uid = `${userInfo.school}_${userInfo.grade}_${userInfo.className}_${userInfo.course}_${userInfo.name}`.replace(/\s/g, "");

      const batch = writeBatch(db);

      // ユーザー情報を保存
      const userRef = doc(db, "guidance", uid);
      batch.set(userRef, {
        school: userInfo.school,
        grade: userInfo.grade,
        className: userInfo.className,
        course: userInfo.course,
        name: userInfo.name,
        number: userInfo.number,
        updateTime: serverTimestamp(),
      }, { merge: true });

      // ガイダンス選択結果を保存（第1志望群・第2志望群、順位付けの学校は「1位」〜。未選択は保存しない）
      for (const recruit of likes) {
        const rank = rankOfId(recruit.recruitId);
        const status = rankMode ? (rank ? rankLabel(rank) : "未選択") : getStatus(recruit.recruitId);
        if (status === "未選択") continue;

        const guidanceRef = doc(collection(db, "guidance", uid, "guidance"), recruit.recruitId);
        batch.set(guidanceRef, {
          companyId: recruit.companyId,
          createdAt: serverTimestamp(),
          name: recruit.name,
          recruitId: recruit.recruitId,
          schedule: "",
          status,
          updateTime: serverTimestamp(),
        });
      }

      await batch.commit();
      setSubmittedDialog(true);

    } catch (e) {
      console.error(e);
      alert("保存に失敗しました。再度お試しください。");
    } finally {
      setLoading(false);
    }
  }

  if (submitted) {
    return (
      <div className={`guidance-page ${Styles.done_page}`}>
        <div className={Styles.done_card}>
          <div className={Styles.dialog_icon}><CheckIcon /></div>
          <div className={Styles.dialog_title}>お疲れさまでした！</div>
          <div className={Styles.dialog_lead}>志望状況の受付が完了しました！</div>
          <p className={Styles.dialog_text}>ガイダンスでは、サロンさんの情報を{"\n"}たくさん聞いてみてください！</p>
        </div>
      </div>
    );
  }

  // 一覧の並び: 志望群は 第1志望群 → 第2志望群 → 未選択、順位付けは 第1志望〜 → 区切り → 未選択
  const groupOrdered = [...likes].sort((a, b) => {
    const order = { 第1志望群: 0, 第2志望群: 1, 未選択: 2 };
    return order[getStatus(a.recruitId)] - order[getStatus(b.recruitId)];
  });
  const byId = (id: string) => likes.find((l) => l.recruitId === id);
  const ranked = slots.flatMap((id) => {
    const r = id ? byId(id) : undefined;
    return r ? [r] : [];
  });
  const unranked = likes.filter((l) => rankOfId(l.recruitId) === null);

  // 並べ替えたあとの順番で志望をつけ直す。区切りより上が第1志望〜、下は未選択。
  // 区切りより上が rankCount を超えたら、あふれた分(いちばん下)は未選択に回す(アプリの _onRankReorder と同じ)。
  function onRankReorder(order: string[]) {
    const dividerAt = order.indexOf(DIVIDER);
    const above = order.slice(0, dividerAt).slice(0, rankCount);
    setRankList(Array.from({ length: rankCount }, (_, i) => above[i] ?? ""));
  }

  // 志望の選択欄。選んでいる志望の色で塗る(第1志望群・第◯志望はピンク、第2志望群はオレンジ、未選択は灰色の文字)。
  // 見た目はサロン名と同じ 14px の文字にし、その上に透明な select を重ねる。select の文字を 16px 未満にすると、
  // iPhone の Safari が押したときに画面を拡大してしまうため。
  const statusSelect = (recruit: RecruitItem) => {
    const id = recruit.recruitId;
    let text: string;
    let color: string;
    let select: React.ReactNode;
    if (rankMode) {
      const rank = rankOfId(id);
      text = rank === null ? "未選択" : rankDisplay(rank);
      color = rank === null ? GRAY : PINK;
      select = (
        <select
          data-testid={`guidance-order-select-${id}`}
          className={Styles.row_select_native}
          aria-label={`${recruit.name}の志望`}
          value={rank === null ? "" : String(rank)}
          onChange={(e) => setRank(id, e.target.value === "" ? null : Number(e.target.value))}
        >
          <option value="">未選択</option>
          {/* ほかのサロンが選んでいる志望を選ぶと入れ替わる(選択肢には相手の名前を出さない) */}
          {slots.map((_, i) => (
            <option key={i} value={String(i + 1)}>{rankDisplay(i + 1)}</option>
          ))}
        </select>
      );
    } else {
      const status = getStatus(id);
      text = status;
      color = status === "第1志望群" ? PINK : status === "第2志望群" ? ORANGE : GRAY;
      select = (
        <select
          data-testid={`guidance-order-select-${id}`}
          className={Styles.row_select_native}
          aria-label={`${recruit.name}の志望`}
          value={status}
          onChange={(e) => setStatus(id, e.target.value as GroupStatus)}
        >
          <option value="未選択">未選択</option>
          {/* 3つ埋まった志望群は選べない */}
          <option value="第1志望群" disabled={firstCount >= 3 && status !== "第1志望群"}>第1志望群</option>
          <option value="第2志望群" disabled={secondCount >= 3 && status !== "第2志望群"}>第2志望群</option>
        </select>
      );
    }
    return (
      // 選んでいる志望は色の背景に白い文字(アプリのラベルと同じ)、未選択は白地に灰色の文字
      <div
        className={`${Styles.row_select} ${color === GRAY ? "" : Styles.row_select_filled}`}
        style={color === GRAY ? { color } : { backgroundColor: color, borderColor: color }}
      >
        <span className={Styles.row_select_text}>{text}</span>
        {select}
      </div>
    );
  };

  const salonRow = (recruit: RecruitItem, handle?: React.ReactNode) => {
    return (
      <div className={Styles.row} data-testid="guidance-order-item" data-recruit-id={recruit.recruitId}>
        <a className={Styles.row_logo_wrap} href={recruitUrl(recruit.recruitId)} target="_blank" rel="noopener noreferrer">
          <img
            src={logoUrl(recruit.companyId)}
            alt=""
            className={Styles.row_logo}
            onError={(e) => { e.currentTarget.src = "/noImage.png"; }}
          />
        </a>
        <a className={Styles.row_name} href={recruitUrl(recruit.recruitId)} target="_blank" rel="noopener noreferrer">
          {recruit.name}
        </a>
        {statusSelect(recruit)}
        {handle}
      </div>
    );
  };

  return (
    <div className={`guidance-page ${Styles.page}`}>
      <div className={Styles.container}>

        {/* × と学校名 */}
        <div className={Styles.appbar}>
          <button onClick={onBack} className={Styles.appbar_close} aria-label="戻る">
            <CloseIcon />
          </button>
          <div className={Styles.appbar_title}>{userInfo.school}</div>
        </div>

        {/* 案内 ＋「確定」 */}
        <div className={Styles.action_pill}>
          <div className={Styles.action_text}>
            {rankMode ? `第1志望〜第${rankCount}志望まで選びましょう。` : "第1志望群を3つ、第2志望群を3つ選択しましょう。"}
          </div>
          <button
            onClick={handleSubmit}
            className={`${Styles.action_btn} ${canSubmit && !loading ? "" : Styles.action_btn_disabled}`}
          >
            {loading ? "保存中..." : "確定"}
          </button>
        </div>

        {rankMode ? (
          <>
            <div className={Styles.drag_hint}>
              <HandleIcon size={16} />
              <span>を上下にドラッグすると、志望の順番を並べ替えられます</span>
            </div>
            <RankReorderList
              items={[...ranked.map((r) => r.recruitId), DIVIDER, ...unranked.map((r) => r.recruitId)]}
              dividerText={ranked.length === 0 ? "ここより上に動かすと志望になります" : "ここから下は未選択"}
              renderRow={(id, handle) => {
                const r = byId(id);
                return r ? salonRow(r, handle) : null;
              }}
              onReorder={onRankReorder}
            />
          </>
        ) : (
          <div className={Styles.list}>
            {groupOrdered.map((r) => <React.Fragment key={r.recruitId}>{salonRow(r)}</React.Fragment>)}
          </div>
        )}
      </div>

      {/* 抜けがあるまま「確定」を押したとき */}
      {rankErrorsOpen && (
        <Sheet onClose={() => setRankErrorsOpen(false)}>
          <div className={Styles.sheet_title}>志望の順番を確認してください</div>
          <div className={Styles.sheet_sub}>第1志望から順に、1つずつ選ぶと確定できます</div>
          <div className={Styles.errors_box}>
            {rankErrors.map((e) => (
              <div key={e} className={Styles.errors_item}>
                <ErrorIcon />
                <span>{e}</span>
              </div>
            ))}
          </div>
          <button className={Styles.sheet_close_btn} onClick={() => setRankErrorsOpen(false)}>閉じる</button>
        </Sheet>
      )}

      {/* 確定のあとの受付完了 */}
      {submittedDialog && (
        <div className={Styles.dialog_backdrop}>
          <div className={Styles.dialog_box}>
            <div className={Styles.dialog_icon}><CheckIcon /></div>
            <div className={Styles.dialog_title}>お疲れさまでした！</div>
            <div className={Styles.dialog_lead}>志望状況の受付が完了しました！</div>
            <p className={Styles.dialog_text}>ガイダンスでは、サロンさんの情報を{"\n"}たくさん聞いてみてください！</p>
            <button className={Styles.dialog_btn} onClick={() => { setSubmittedDialog(false); setSubmitted(true); }}>閉じる</button>
          </div>
        </div>
      )}
    </div>
  );
}

// 下から出るシート(右上に ×)
function Sheet({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return (
    <div className={Styles.sheet_backdrop} onClick={onClose}>
      <div className={Styles.sheet} onClick={(e) => e.stopPropagation()}>
        <div className={Styles.sheet_close_row}>
          <button className={Styles.sheet_close} onClick={onClose} aria-label="閉じる"><CloseIcon /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ≡ をつかんで上下に動かせるリスト。スマホでも動くよう、HTML のドラッグではなくポインタの動きで並べ替える。
function RankReorderList({ items, dividerText, renderRow, onReorder }: {
  items: string[];
  dividerText: string;
  renderRow: (id: string, handle: React.ReactNode) => React.ReactNode;
  onReorder: (order: string[]) => void;
}) {
  const refs = useRef<(HTMLDivElement | null)[]>([]);
  const [ drag, setDrag ] = useState<{ index: number; dy: number; over: number } | null>(null);
  const dragInfo = useRef<{ index: number; startY: number; startScroll: number; rects: { top: number; height: number }[] } | null>(null);
  const autoScroll = useRef<number | undefined>(undefined);
  const lastY = useRef(0);

  const update = (clientY: number) => {
    const info = dragInfo.current;
    if (!info) return;
    lastY.current = clientY;
    const dy = clientY - info.startY + (window.scrollY - info.startScroll);
    const me = info.rects[info.index];
    const center = me.top + me.height / 2 + dy;
    let over = 0;
    info.rects.forEach((r, i) => {
      if (i !== info.index && r.top + r.height / 2 < center) over++;
    });
    setDrag({ index: info.index, dy, over });
  };

  const start = (index: number) => (e: React.PointerEvent) => {
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragInfo.current = {
      index,
      startY: e.clientY,
      startScroll: window.scrollY,
      // ページ上の位置で覚える(ドラッグ中にスクロールしてもずれないように)
      rects: refs.current.map((el) => {
        const r = el?.getBoundingClientRect();
        return { top: (r?.top ?? 0) + window.scrollY, height: r?.height ?? 0 };
      }),
    };
    setDrag({ index, dy: 0, over: index });
    // 画面の上下の端に近づいたら、少しずつスクロールする
    autoScroll.current = window.setInterval(() => {
      const y = lastY.current;
      const edge = 80;
      if (y < edge) window.scrollBy(0, -8);
      else if (y > window.innerHeight - edge) window.scrollBy(0, 8);
      else return;
      update(y);
    }, 16);
    lastY.current = e.clientY;
  };

  const end = () => {
    window.clearInterval(autoScroll.current);
    const info = dragInfo.current;
    dragInfo.current = null;
    if (drag && info && drag.over !== info.index) {
      const next = [...items];
      const [moved] = next.splice(info.index, 1);
      next.splice(drag.over, 0, moved);
      onReorder(next);
    }
    setDrag(null);
  };

  // つかんでいる行の高さ分だけ、あいだの行をずらして「ここに入る」を見せる
  const shiftOf = (i: number) => {
    if (!drag || i === drag.index || !dragInfo.current) return 0;
    const h = dragInfo.current.rects[drag.index].height;
    if (drag.index < i && i <= drag.over) return -h;
    if (drag.over <= i && i < drag.index) return h;
    return 0;
  };

  return (
    <div className={Styles.list}>
      {items.map((id, i) => {
        const dragging = drag?.index === i;
        const style: React.CSSProperties = dragging
          ? { transform: `translateY(${drag!.dy}px)`, zIndex: 5, position: "relative" }
          : { transform: `translateY(${shiftOf(i)}px)`, transition: drag ? "transform .15s" : undefined };
        if (id === DIVIDER) {
          return (
            <div key={DIVIDER} ref={(el) => { refs.current[i] = el; }} className={Styles.divider} style={style}>
              <span className={Styles.divider_line} />
              <span className={Styles.divider_text}>{dividerText}</span>
              <span className={Styles.divider_line} />
            </div>
          );
        }
        const handle = (
          <span
            className={Styles.handle}
            onPointerDown={start(i)}
            onPointerMove={(e) => dragInfo.current && update(e.clientY)}
            onPointerUp={end}
            onPointerCancel={end}
            aria-label="ドラッグして並べ替え"
          >
            <HandleIcon size={24} />
          </span>
        );
        return (
          <div
            key={id}
            ref={(el) => { refs.current[i] = el; }}
            className={dragging ? Styles.row_dragging : undefined}
            style={style}
          >
            {renderRow(id, handle)}
          </div>
        );
      })}
    </div>
  );
}

const CloseIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1B1B1B" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
);
const HandleIcon = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#6B6B6B" strokeWidth="2" strokeLinecap="round"><path d="M4 9h16M4 15h16" /></svg>
);
const CheckIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#E44E70" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 5 5 9-10" /></svg>
);
const ErrorIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="#E44E70" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="12" cy="12" r="10" /><path d="M12 7v6M12 16.5v.5" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" /></svg>
);
